import { Card } from "components/base/Card"
import { Input } from "components/base/Input"
import { Select, Option } from "components/base/Select"
import { Button } from "components/base/Button"
import {AppUser, useUser} from "context/user-context"
import React, { useState, useEffect, FormEvent } from "react"
import { deleteUtilisateur, getGroupes, Groupes, ProfilRequest, updatePassword, updateUtilisateur, Utilisateur } from "services/apiUtilisateurs"
import { Title } from "components/base/Title"
import { Dialog } from "components/base/Dialog"
import { getLivraisonEnCours } from "services/apiLivraisons"
import {InformationCircleIcon, TrashIcon} from "@heroicons/react/24/outline"
import {PASSWORD_REQUIREMENT, validatePassword} from "../../utils/utils";



interface PasswordFormData {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export const Profil: React.FC = () => {
  const { user, setUser, logout } = useUser();
  const [groupes, setGroupes] = useState<Groupes[]>([]);
  const [loadingGroupes, setLoadingGroupes] = useState<boolean>(true);
  const [alertGroupChange, setAlertGroupChange] = useState<boolean>(false);
  const [alertGroupChangeConfirm, setAlertGroupChangeConfirm] = useState<boolean>(false);
  const [referent, setReferent] = useState<Utilisateur | null>(null);
	const [alertDeleteAccount, setAlertDeleteAccount] = useState<boolean>(false);
	const [errorPsw, setErrorPsw] = useState<string>("");

	const [saveDone, setSaveDone] = useState<{type: "data" | "mdp", state: boolean }>({ type: "data", state: false });
	const isAdmin = !!user && user.role === 'admin';

  // État pour le formulaire de profil
  const [profilData, setProfilData] = useState<ProfilRequest>({
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    group_id: null,
  });

  // État pour le formulaire de mot de passe
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  // Charger les groupes disponibles
  useEffect(() => {
    const fetchGroupes = async () => {
      try {
        const data = await getGroupes();
        setGroupes(data);
      } catch (error) {
        console.error("Erreur lors du chargement des groupes:", error);
      } finally {
        setLoadingGroupes(false);
      }
    };
    fetchGroupes();

    const fetchReferent = async () => {
      try {
        const data = await getLivraisonEnCours(user?.group_id ?? 0);
        const referent = data.referent;
        setReferent(referent ?? null);
      } catch (error) {
        console.error("Erreur lors du chargement du référent:", error);
      }
    };
    fetchReferent();
  }, []);

  // Initialiser les données du profil quand l'utilisateur est chargé
  useEffect(() => {
    if (user) {
      setProfilData({
        nom: user.nom || "",
        prenom: user.prenom || "",
        telephone: user.telephone || "",
        email: user.email || "",
        group_id: user.group_id,
      });
    }
  }, [user]);

  // Gestionnaire de changement pour les inputs du profil
  const handleProfilInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfilData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gestionnaire de changement pour le select du groupe
  const handleGroupeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setProfilData(prev => ({
      ...prev,
      group_id: value ? parseInt(value) : null,
    }));
  };

  // Gestionnaire de changement pour le mot de passe
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Soumission du formulaire de profil
  const handleProfilSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Objet à envoyer au backend
    const dataToSend = {
      nom: profilData.nom,
      prenom: profilData.prenom,
      telephone: profilData.telephone,
      email: profilData.email,
      group_id: profilData.group_id,
    };

	  try {
      await updateUtilisateur(user?.id ?? 0, dataToSend);
		  setUser({ ...user, ...dataToSend } as AppUser);
	    setSaveDone({type: "data", state: true})
	    setTimeout(() => {
		    setSaveDone({type: "data", state: false})
	    }, 2000);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
    }
  };

  // Soumission du formulaire de mot de passe
  const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
		setErrorPsw("");

    // Vérifier que les mots de passe correspondent
    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setErrorPsw("Les mots de passe ne correspondent pas");
      return;
    }

	  const validationErreur = validatePassword(passwordData.new_password);
	  if (validationErreur !== null) {
		  setErrorPsw(validationErreur);
		  return;
		}

    // Objet à envoyer au backend
    const dataToSend = {
      current_password: passwordData.current_password,
      password: passwordData.new_password,
      password_confirmation: passwordData.new_password_confirmation,
    };

    try {
      await updatePassword(dataToSend);
      setSaveDone({type: "mdp", state: true})
      setTimeout(() => {
        setSaveDone({type: "mdp", state: false})
      }, 2000);

      // Réinitialiser le formulaire après soumission
      setPasswordData({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (error) {
      setErrorPsw("Erreur lors du changement de mot de passe. Vérifiez votre mot de passe actuel.");
    }
  };

  // Options pour le select des groupes
  const groupeOptions: Option[] = groupes.map(groupe => ({
    label: groupe.groupe,
    value: groupe.id.toString(),
  }));

  if (!user) {
    return null;
  }

  const handleDeleteAccount = async () => {
    try {
      const response = await deleteUtilisateur();
      if (response) {
        logout();
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <Title type="h2">Mon Profil</Title>
        {!isAdmin && (
          <Button variant="alert-outline" onClick={() => {
            setAlertDeleteAccount(true);
          }}
            icon={<TrashIcon className="w-5 h-5" />}
          />
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <Card className="flex flex-col items-center justify-center gap-4 flex-1 px-4 2xl:px-16 lg:max-w-[66%]">
          {/* Bloc 1: Informations personnelles */}
          <Title type="h4" className="mb-4" centered>Informations personnelles</Title>
          <form onSubmit={handleProfilSubmit} className="w-full">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Input
                label="Nom"
                name="nom"
                type="text"
                value={profilData.nom}
                onChange={handleProfilInputChange}
                width="100%"
                required
              />

              <Input
                label="Prénom"
                name="prenom"
                type="text"
                value={profilData.prenom}
                onChange={handleProfilInputChange}
                width="100%"
                required
              />

              <Input
                label="Téléphone"
                name="telephone"
                type="tel"
                value={profilData.telephone}
                onChange={handleProfilInputChange}
                width="100%"
              />

              <Input
                label="Email"
                name="email"
                type="email"
                value={profilData.email}
                onChange={handleProfilInputChange}
                width="100%"
                required
              />

              <Select
                label="Groupe"
                name="groupe"
                value={profilData.group_id?.toString() || ""}
                onClick={() => !alertGroupChangeConfirm && setAlertGroupChange(true)}
                onChange={handleGroupeChange}
                options={groupeOptions}
                disabled={loadingGroupes}
                width="100%"
              />
            </div>

	          {saveDone.state && saveDone.type === "data" &&
		          <p className="border border-success-200 bg-success-100 p-2 rounded-md mt-6">
			          Les données ont bien été enregistrées
		          </p>
	          }

            <div className="flex justify-center mt-6">
              <Button type="submit" variant="primary">
                Enregistrer les modifications
              </Button>
            </div>
          </form>
        </Card>
        <Card className="flex flex-col items-center gap-4 px-4 2xl:px-16 lg:max-w-[33%]">
          {/* Bloc 2: Changement de mot de passe */}
          <Title type="h4" className="mb-4" centered>Changer le mot de passe</Title>
          <form onSubmit={handlePasswordSubmit} className="space-y-4 w-full">
            <Input
              label="Mot de passe actuel"
              name="current_password"
              type="password"
              value={passwordData.current_password}
              onChange={handlePasswordChange}
              width="100%"
              required
            />

            <Input
              label="Nouveau mot de passe"
              name="new_password"
              type="password"
              value={passwordData.new_password}
              onChange={handlePasswordChange}
              width="100%"
              required
            />

	          <div>
	            <Input
	              label="Confirmer le nouveau mot de passe"
	              name="new_password_confirmation"
	              type="password"
	              value={passwordData.new_password_confirmation}
	              onChange={handlePasswordChange}
	              width="100%"
	              required
	            />

		          <div className="text-sm text-gray-500">
			          {errorPsw && (
				          <span className="text-alert text-sm px-2">{errorPsw}</span>
			          )}
			          <div className="flex gap-2"><InformationCircleIcon className="w-4 h-4 mt-1" /><span className="flex-1">{PASSWORD_REQUIREMENT}</span></div>
		          </div>
	          </div>

	          {saveDone.state && saveDone.type === "mdp" &&
		          <p className="border border-success-200 bg-success-100 p-2 rounded-md mt-6">
			          Les données ont bien été enregistrées
		          </p>
	          }

            <div className="flex justify-center !mt-6">
              <Button type="submit" variant="secondary">
                Changer le mot de passe
              </Button>
            </div>
          </form>
        </Card>
      </div>
      {referent && (
        <>
          <Title type="h2" className="my-4">Mon référent pour la prochaine livraison</Title>
          <Card className="leading-relaxed">
            <p><span className="text-gray-600 font-bold">Nom : </span>{referent?.nom_complet}</p>
            <p><span className="text-gray-600 font-bold">Email : </span>{referent?.email}</p>
            <p><span className="text-gray-600 font-bold">Téléphone : </span>{referent?.telephone}</p>
          </Card>
        </>
      )}

      {alertDeleteAccount && (
        <Dialog
          open={alertDeleteAccount}
          onClose={() => setAlertDeleteAccount(false)}
          title="Attention"
          confirmLabel="Supprimer"
          confirmVariant="alert"
          onConfirm={handleDeleteAccount}
        >
          <p className="mb-4">
            Cette action est <strong>irréversible</strong>. Vous ne pourrez plus accéder à votre compte ni récupérer vos données.
          </p>
          <p>Voulez-vous vraiment supprimer votre compte ?</p>
        </Dialog>
      )}

      {alertGroupChange && (
        <Dialog
          open={alertGroupChange}
          onClose={() => setAlertGroupChange(false)}
          title="Attention"
          message="Changer de groupe déplacera toutes vos commandes prochaines dans le nouveau groupe."
          confirmLabel="Je suis d'accord"
          onConfirm={() => setAlertGroupChangeConfirm(true)}
        />
      )}
    </>
  )
}
